/**
 * utils/oauth.ts
 * OAuth2 PKCE utility functions
 *
 * Provides helper functions for OAuth2 PKCE (Proof Key for Code Exchange) flow.
 * PKCE is the secure OAuth flow for Single Page Applications (no client secret).
 */

/**
 * Generate cryptographically random code verifier for PKCE
 *
 * Creates a URL-safe random string between 43-128 characters as required
 * by RFC 7636. Uses Web Crypto API for cryptographically secure randomness.
 *
 * @returns URL-safe random string (64 characters)
 *
 * @example
 * ```typescript
 * const verifier = generateCodeVerifier();
 * // "dBjftJeZ4CVP-mB92K27uhbUJU1p1r_wW1gFWFOEjXk"
 * ```
 */
export function generateCodeVerifier(): string {
  // Generate 32 random bytes (will become 43+ characters when base64url encoded)
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);

  // Convert to base64url format (URL-safe, no padding)
  return base64UrlEncode(array);
}

/**
 * Generate SHA-256 code challenge from verifier
 *
 * Creates PKCE code challenge by hashing the verifier with SHA-256
 * and encoding as base64url. This is sent in the authorization request.
 *
 * @param verifier - Code verifier string
 * @returns Promise resolving to base64url encoded SHA-256 hash
 *
 * @example
 * ```typescript
 * const verifier = generateCodeVerifier();
 * const challenge = await generateCodeChallenge(verifier);
 * // "E9Melhoa2OwvFrEMTJguCHaoeK1t8URWbuGJSstw-cM"
 * ```
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  // Convert verifier to Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);

  // Hash with SHA-256
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = new Uint8Array(hashBuffer);

  // Convert to base64url format
  return base64UrlEncode(hashArray);
}

/**
 * Generate random state parameter for CSRF protection
 *
 * Creates a cryptographically random state string to prevent CSRF attacks.
 * The state is sent in the auth request and verified in the callback.
 *
 * @returns Random state string
 *
 * @example
 * ```typescript
 * const state = generateState();
 * // "f45jT9K2mP3nQ8rS1vW4xY5z"
 * ```
 */
export function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Encode Uint8Array as base64url string
 *
 * Converts binary data to base64url format (URL-safe, no padding).
 * Used for PKCE code verifier, challenge, and state parameters.
 *
 * @param buffer - Uint8Array to encode
 * @returns Base64url encoded string
 */
function base64UrlEncode(buffer: Uint8Array): string {
  // Convert to base64
  const base64 = btoa(String.fromCharCode(...buffer));

  // Convert to base64url: replace +/= with -_
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Build OAuth authorization URL with PKCE parameters
 *
 * Constructs the complete authorization URL for redirecting the user
 * to the provider's consent screen.
 *
 * @param authEndpoint - Provider's authorization endpoint
 * @param clientId - OAuth client ID
 * @param redirectUri - Callback URL
 * @param scopes - Requested scopes array
 * @param codeChallenge - PKCE code challenge
 * @param state - CSRF protection state
 * @param additionalParams - Optional additional parameters
 * @returns Complete authorization URL
 *
 * @example
 * ```typescript
 * const url = buildAuthorizationUrl(
 *   'https://accounts.google.com/o/oauth2/v2/auth',
 *   'my-client-id',
 *   'http://localhost:5173/oauth/callback',
 *   ['https://www.googleapis.com/auth/drive.file'],
 *   challenge,
 *   state
 * );
 * ```
 */
export function buildAuthorizationUrl(
  authEndpoint: string,
  clientId: string,
  redirectUri: string,
  scopes: string[],
  codeChallenge: string,
  state: string,
  additionalParams?: Record<string, string>
): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: scopes.join(' '),
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    ...additionalParams,
  });

  return `${authEndpoint}?${params.toString()}`;
}

/**
 * Open OAuth popup window
 *
 * Opens a centered popup window for OAuth consent flow.
 * Returns the window reference for monitoring.
 *
 * @param url - Authorization URL to open
 * @param windowName - Window name (default: 'oauth')
 * @returns Popup window reference or null if blocked
 *
 * @example
 * ```typescript
 * const popup = openOAuthPopup(authUrl);
 * if (!popup) {
 *   console.error('Popup blocked by browser');
 * }
 * ```
 */
export function openOAuthPopup(
  url: string,
  windowName = 'oauth'
): Window | null {
  // Popup dimensions
  const width = 600;
  const height = 700;

  // Center popup on screen
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'toolbar=no',
    'menubar=no',
    'scrollbars=yes',
    'resizable=yes',
  ].join(',');

  return window.open(url, windowName, features);
}

/**
 * Parse OAuth callback URL parameters
 *
 * Extracts authorization code, state, and error from callback URL.
 * Used to handle the redirect after user consent.
 *
 * @param url - Callback URL (window.location.href)
 * @returns Parsed parameters
 *
 * @example
 * ```typescript
 * const params = parseOAuthCallback(window.location.href);
 * if (params.error) {
 *   console.error('OAuth error:', params.error);
 * } else if (params.code) {
 *   // Exchange code for tokens
 * }
 * ```
 */
export function parseOAuthCallback(url: string): {
  code: string | null;
  state: string | null;
  error: string | null;
  errorDescription: string | null;
} {
  const urlObj = new URL(url);
  const params = urlObj.searchParams;

  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  };
}

/**
 * Monitor popup window and wait for callback
 *
 * Polls popup window to detect when it navigates to the callback URL.
 * Returns the callback URL when detected or rejects if popup is closed.
 *
 * @param popup - Popup window reference
 * @param redirectUri - Expected callback URL
 * @param pollInterval - Polling interval in ms (default: 500)
 * @param timeout - Maximum wait time in ms (default: 300000 = 5 minutes)
 * @returns Promise resolving to callback URL
 *
 * @example
 * ```typescript
 * const popup = openOAuthPopup(authUrl);
 * try {
 *   const callbackUrl = await monitorPopupCallback(popup, redirectUri);
 *   const params = parseOAuthCallback(callbackUrl);
 *   // Handle params
 * } catch (error) {
 *   console.error('OAuth flow cancelled or timed out');
 * }
 * ```
 */
export function monitorPopupCallback(
  popup: Window | null,
  redirectUri: string,
  pollInterval = 500,
  timeout = 300000
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!popup) {
      reject(new Error('Popup window is null'));
      return;
    }

    const startTime = Date.now();
    const redirectOrigin = new URL(redirectUri).origin;

    const interval = setInterval(() => {
      // Check if popup was closed
      if (popup.closed) {
        clearInterval(interval);
        reject(new Error('OAuth popup was closed by user'));
        return;
      }

      // Check for timeout
      if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        popup.close();
        reject(new Error('OAuth flow timed out'));
        return;
      }

      // Try to read popup URL (will fail if different origin due to CORS)
      try {
        const popupUrl = popup.location.href;

        // Check if popup navigated to callback URL
        if (popupUrl.startsWith(redirectUri) || popup.location.origin === redirectOrigin) {
          clearInterval(interval);
          popup.close();
          resolve(popupUrl);
        }
      } catch (_error) {
        // Cross-origin error - popup is still on provider's domain
        // Continue polling
      }
    }, pollInterval);
  });
}
